import { useState } from "react";
import { Modal } from "features/Modal";
import { Button, PlusSmall, Minus, Input } from "components";
import { MapPicker } from "components/MapPicker";
import { Language } from "helpers";
import { WithActions } from "components/WithActions";
import { t } from "i18next";

type ModalStep = "geo" | "municipality" | "type" | "payment" | "success" | "error";

export interface FormData {
  parking_name_mk: string;
  parking_name_al: string;
  parking_name_en: string;
  longitude: string;
  latitude: string;
  municipality_id: number;
  area_type: string;
  total_parking_slots: number;
  payment_options: number[];
  amenities: number[];
  address: string;
}

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  section: "geo" | "municipality" | "type" | "payment";
  initialStep?: ModalStep;
  formData: FormData;
  updateForm: (key: keyof FormData, value: string | number | number[]) => void;
  onSuccess?: () => void;
}
export const ParkingEditSeparateModal = ({ open, setOpen, section, formData, updateForm, onSuccess }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [useCoords, setUseCoords] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const toggleItem = (key: keyof FormData, id: number) => {
    const current = formData[key] as number[];
    const updated = current.includes(id) ? current.filter((val) => val !== id) : [...current, id];
    updateForm(key, updated);
  };

  const handleToggleType = (type: string) => {
    updateForm("area_type", formData.area_type === type ? "" : type);
  };

  const validateSection = (): boolean => {
    switch (section) {
      case "geo":
        if (!formData.latitude.trim() || !formData.longitude.trim()) {
          setErrorMessage(t("parkingManagement.latitudeAndLongitudeRequired"));
          return false;
        }
        break;
      case "municipality":
        if (!formData.parking_name_mk.trim() || !formData.parking_name_al.trim() || !formData.parking_name_en.trim()) {
          setErrorMessage(t("parkingManagement.allParkingNameFieldsAreRequired"));
          return false;
        }
        if (!formData.municipality_id) {
          setErrorMessage(t("parkingManagement.municipalityIdRequired"));
          return false;
        }
        break;
      case "type":
        if (!formData.area_type || !formData.total_parking_slots) {
          setErrorMessage(t("parkingManagement.parkingTypeAndSlotsRequired"));
          return false;
        }
        break;
      case "payment":
        if (formData.payment_options.length === 0) {
          setErrorMessage(t("parkingManagement.atLeastOnePaymentOptionIsRequired"));
          return false;
        }
        break;
    }
    setErrorMessage(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateSection()) return;

    setIsLoading(true);
    try {
      // Here you would typically call your API to update the parking
      // await updateParking({...formData});
      if (onSuccess) onSuccess();
      setOpen(false);
    } catch (error: any) {
      setErrorMessage(error?.message || t("parkingManagement.failedUpdateParking"));
    } finally {
      setIsLoading(false);
    }
  };

  const onClose = () => {
    setOpen(false);
    setErrorMessage(null);
  };

  const renderSection = () => {
    switch (section) {
      case "geo":
        return (
          <div>
            <h3 className="mb-4 text-xs font-light uppercase text-primary">
              {t("parkingManagement.editParking")} <span className="font-bold">(Location)</span>
            </h3>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={useCoords} onChange={() => setUseCoords(true)} className="h-4 w-4" />
                <span>{t("parkingManagement.useCoordinates")}</span>
              </div>

              <div className="flex gap-2">
                <Input
                  value={formData.latitude}
                  onChange={(e) => updateForm("latitude", e.target.value)}
                  placeholder={t("parkingManagement.latitude")}
                  className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                  type="text"
                  variant="modal"
                  readOnly={!useCoords}
                />
                <Input
                  value={formData.longitude}
                  onChange={(e) => updateForm("longitude", e.target.value)}
                  placeholder={t("parkingManagement.longitude")}
                  className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                  type="text"
                  variant="modal"
                  readOnly={!useCoords}
                />
              </div>

              <div className="flex gap-2 text-xs text-gray-500">
                <a href="https://www.latlong.net/" target="_blank" rel="noreferrer" className="underline">
                  {t("parkingManagement.getCoordinates")}
                </a>
                <a href="https://maps.google.com" target="_blank" rel="noreferrer" className="underline">
                  {t("parkingManagement.goToMaps")}
                </a>
              </div>

              <div className="flex items-center gap-2 pt-4">
                <input type="checkbox" checked={!useCoords} onChange={() => setUseCoords(false)} className="h-4 w-4" />
                <span>{t("parkingManagement.useMap")}</span>
              </div>

              {!useCoords && (
                <div className="space-y-2">
                  <MapPicker
                    onSelect={(lat, lng) => {
                      updateForm("latitude", lat.toString());
                      updateForm("longitude", lng.toString());
                    }}
                  />
                  <p className="text-xs text-gray-500">{t("parkingManagement.clickToUseMap")}</p>
                </div>
              )}
            </div>
          </div>
        );

      case "municipality":
        return (
          <div>
            <h3 className="mb-4 text-xs font-light uppercase text-primary">
              {t("parkingManagement.editParking")} <span className="font-bold">(Municipality)</span>
            </h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold">{t("parkingManagement.parkingNameMk")}</h4>
                <Input
                  value={formData.parking_name_mk}
                  onChange={(e) => updateForm("parking_name_mk", e.target.value)}
                  placeholder={t("parkingManagement.enterParkingNameInMacedonian")}
                  className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                  type="text"
                  variant="modal"
                />
              </div>
              <div>
                <h4 className="font-semibold">{t("parkingManagement.parkingNameAl")}</h4>
                <Input
                  value={formData.parking_name_al}
                  onChange={(e) => updateForm("parking_name_al", e.target.value)}
                  placeholder={t("parkingManagement.enterParkingNameInAlbanian")}
                  className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                  type="text"
                  variant="modal"
                />
              </div>
              <div>
                <h4 className="font-semibold">{t("parkingManagement.parkingNameEn")}</h4>
                <Input
                  value={formData.parking_name_en}
                  onChange={(e) => updateForm("parking_name_en", e.target.value)}
                  placeholder={t("parkingManagement.enterParkingNameInEnglish")}
                  className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                  type="text"
                  variant="modal"
                />
              </div>
              <div>
                <h4 className="font-semibold">{t("parkingManagement.municipalityId")}</h4>
                <Input
                  value={formData.municipality_id.toString()}
                  onChange={(e) => updateForm("municipality_id", Number(e.target.value))}
                  placeholder={t("parkingManagement.enterMunicipalityId")}
                  className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                  type="number"
                  variant="modal"
                />
              </div>
            </div>
          </div>
        );

      case "type":
        return (
          <div>
            <h3 className="mb-4 text-xs font-light uppercase text-primary">
              {t("parkingManagement.editParking")} <span className="font-bold">(Type & Slots)</span>
            </h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold">{t("parkingManagement.parkingType")}*</h4>
                <div className="flex gap-4">
                  {["Open", "Closed"].map((type) => {
                    const selected = formData.area_type === type;
                    return (
                      <button
                        key={type}
                        onClick={() => handleToggleType(type)}
                        className={`flex h-[48px] min-w-[120px] items-center rounded border ${
                          selected ? "border-green-600 bg-green-600 text-white" : "border-[#1C67AD] text-[#1C67AD]"
                        }`}
                      >
                        <div className="flex-1 px-4 text-left font-semibold">{type}</div>
                        <div
                          className={`flex h-full w-[40px] items-center justify-center ${
                            selected ? "bg-green-700" : "bg-primary"
                          }`}
                        >
                          {selected ? <Minus className="text-white" /> : <PlusSmall className="text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="font-semibold">{t("parkingManagement.totalParkingSlots")}*</h4>
                <Input
                  value={formData.total_parking_slots.toString()}
                  onChange={(e) => updateForm("total_parking_slots", Number(e.target.value))}
                  placeholder={t("parkingManagement.enterTotalParkingSlots")}
                  className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                  type="number"
                  variant="modal"
                />
              </div>
            </div>
          </div>
        );

      case "payment":
        return (
          <div>
            <h3 className="mb-4 text-xs font-light uppercase text-primary">
              {t("parkingManagement.editParking")} <span className="font-bold">(Payment & Amenities)</span>
            </h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold">{t("parkingManagement.paymentOptions")}*</h4>
                <div className="flex flex-wrap gap-4">
                  {[
                    { id: 1, label: "Cash" },
                    { id: 2, label: "Card" },
                  ].map(({ id, label }) => {
                    const selected = formData.payment_options.includes(id);
                    return (
                      <button
                        key={id}
                        onClick={() => toggleItem("payment_options", id)}
                        className={`flex h-[48px] min-w-[120px] items-center rounded border ${
                          selected ? "border-green-600 bg-green-600 text-white" : "border-[#1C67AD] text-[#1C67AD]"
                        }`}
                      >
                        <div className="flex-1 px-4 text-left font-semibold">{label}</div>
                        <div
                          className={`flex h-full w-[40px] items-center justify-center ${
                            selected ? "bg-green-700" : "bg-primary"
                          }`}
                        >
                          {selected ? <Minus className="text-white" /> : <PlusSmall className="text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="font-semibold">{t("parkingManagement.amenities")}</h4>
                <div className="flex flex-wrap gap-4">
                  {[
                    { id: 1, label: "Wheelchair" },
                    { id: 2, label: "Cameras" },
                    { id: 3, label: "Fire Alarm" },
                  ].map(({ id, label }) => {
                    const selected = formData.amenities.includes(id);
                    return (
                      <button
                        key={id}
                        onClick={() => toggleItem("amenities", id)}
                        className={`flex h-[48px] min-w-[140px] items-center rounded border ${
                          selected ? "border-green-600 bg-green-600 text-white" : "border-[#1C67AD] text-[#1C67AD]"
                        }`}
                      >
                        <div className="flex-1 px-4 text-left font-semibold">{label}</div>
                        <div
                          className={`flex h-full w-[40px] items-center justify-center ${
                            selected ? "bg-green-700" : "bg-primary"
                          }`}
                        >
                          {selected ? <Minus className="text-white" /> : <PlusSmall className="text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} withClose={true} onlyCloseButton>
      <div className="flex flex-col gap-6 p-4">
        {renderSection()}

        {errorMessage && <p className="mt-1 text-xs text-red-500">{errorMessage}</p>}
        <WithActions
          closeButton="Cancel"
          setOpen={onClose}
          additionalButtons={
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          }
        />
      </div>
    </Modal>
  );
};
